import { Plus, Send, Settings2 } from "lucide-react";
import { useRefresh } from "ra-core";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { formatLongDate } from "../gulaConstants";
import { MoneyTabs } from "../money/MoneyTabs";
import type { AccExpense, AccIncome, AccSupplier } from "./accounting";
import { AccountingSettingsDialog } from "./AccountingSettingsDialog";
import { AccountingSummaryView } from "./AccountingSummaryView";
import { ExpenseDialog } from "./ExpenseDialog";
import { ExpensesView } from "./ExpensesView";
import { IncomeDialog } from "./IncomeDialog";
import { IncomesView } from "./IncomesView";
import { InvoiceUploader } from "./InvoiceUploader";
import {
  buildAlerts,
  buildSummary,
  inPeriod,
  quarterOf,
  suppliersOver347,
  type Period,
  type Quarter,
} from "./summary";
import { SendToGestorDialog } from "./SendToGestorDialog";
import { SupplierDialog } from "./SupplierDialog";
import { SuppliersView } from "./SuppliersView";
import { useAccountingData } from "./useAccountingData";

const VIEWS = [
  { value: "resumen", label: "Resumen" },
  { value: "gastos", label: "Gastos" },
  { value: "ingresos", label: "Ingresos" },
  { value: "proveedores", label: "Proveedores" },
] as const;
type View = (typeof VIEWS)[number]["value"];

const FIRST_YEAR = 2025;

const todayDay = () => new Date().toLocaleDateString("en-CA");

/** Dinero -> Contabilidad: income, purchase invoices, VAT and the accountant's pack. */
export const AccountingPage = () => {
  const refresh = useRefresh();
  const [params, setParams] = useSearchParams();
  const today = todayDay();
  const currentYear = Number(today.slice(0, 4));

  const year = Number(params.get("anio")) || currentYear;
  const quarterParam = params.get("t");
  const quarter: Quarter | null =
    quarterParam === "año"
      ? null
      : ((Number(quarterParam) || quarterOf(today)) as Quarter);
  const period: Period = { year, quarter };
  const view: View =
    (VIEWS.find((option) => option.value === params.get("vista"))?.value as
      | View
      | undefined) ?? "resumen";
  const review = params.get("revisar") === "1";

  const setParam = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) =>
      value == null ? next.delete(key) : next.set(key, value),
    );
    setParams(next, { replace: true });
  };

  const {
    suppliers,
    expenses,
    incomeRows,
    settings,
    submissions,
    isPending,
    error,
  } = useAccountingData();
  const [expenseDialog, setExpenseDialog] = useState<{
    open: boolean;
    expense?: AccExpense | null;
  }>({ open: false });
  const [incomeDialog, setIncomeDialog] = useState<{
    open: boolean;
    income?: AccIncome | null;
  }>({ open: false });
  const [supplierDialog, setSupplierDialog] = useState<{
    open: boolean;
    supplier?: AccSupplier | null;
  }>({ open: false });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const periodExpenses = expenses.filter((e) =>
    inPeriod(e.invoice_date, period),
  );
  const periodIncomes = incomeRows.filter((row) => inPeriod(row.day, period));
  const summary = buildSummary(incomeRows, expenses, period);
  const lastSent = submissions.find(
    (row) =>
      row.period_year === period.year &&
      (row.period_quarter ?? null) === (period.quarter ?? null),
  );
  const alerts = buildAlerts(
    suppliers,
    expenses,
    period,
    today,
    lastSent?.sent_at,
  );
  const toReview = periodExpenses.filter((e) => e.status === "review").length;
  const openExpense = (expense: AccExpense) =>
    setExpenseDialog({ open: true, expense });

  const years = Array.from(
    { length: currentYear - FIRST_YEAR + 1 },
    (_, index) => currentYear - index,
  );

  return (
    <div className="flex flex-col gap-5 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Contabilidad</h1>
          <p className="text-sm text-muted-foreground">
            Ingresos de Stripe automáticos, gastos con su factura, IVA del
            trimestre y paquete para el gestor
          </p>
        </div>
        <MoneyTabs />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={String(year)}
            onValueChange={(value) => setParam({ anio: value })}
          >
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1 rounded-lg bg-muted p-1 text-sm">
            {([1, 2, 3, 4] as Quarter[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setParam({ t: String(option) })}
                className={cn(
                  "rounded-md px-3 py-1 font-medium",
                  quarter === option
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {option}T
              </button>
            ))}
            <button
              type="button"
              onClick={() => setParam({ t: "año" })}
              className={cn(
                "rounded-md px-3 py-1 font-medium",
                quarter == null
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Año
            </button>
          </div>
          {lastSent ? (
            <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200">
              Enviado al gestor el {formatLongDate(lastSent.sent_at)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setExpenseDialog({ open: true })}
          >
            <Plus className="h-4 w-4" />
            Gasto a mano
          </Button>
          <Button
            variant="outline"
            onClick={() => setIncomeDialog({ open: true })}
          >
            <Plus className="h-4 w-4" />
            Ingreso
          </Button>
          <Button disabled={isPending} onClick={() => setSendOpen(true)}>
            <Send className="h-4 w-4" />
            Enviar al gestor
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Ajustes de contabilidad"
            title="Ajustes: gestor y Meta Ads"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b text-sm">
        {VIEWS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setParam({ vista: option.value, revisar: null })}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 font-medium",
              view === option.value
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
            {option.value === "gastos" && periodExpenses.length ? (
              <span className="ml-1 text-xs text-muted-foreground">
                {periodExpenses.length}
              </span>
            ) : null}
            {option.value === "gastos" && toReview ? (
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-xs text-amber-900 dark:bg-amber-400/15 dark:text-amber-200">
                {toReview} por revisar
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {error ? (
        <p className="text-sm text-destructive">
          No se han podido cargar los datos de contabilidad.
        </p>
      ) : isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : view === "resumen" ? (
        <div className="flex flex-col gap-4">
          <InvoiceUploader onChanged={refresh} onOpen={openExpense} />
          <AccountingSummaryView
            summary={summary}
            alerts={alerts}
            period={period}
            suppliers347={
              quarter == null ? suppliersOver347(expenses, year) : []
            }
            onReview={() => setParam({ vista: "gastos", revisar: "1" })}
          />
        </div>
      ) : view === "gastos" ? (
        <div className="flex flex-col gap-4">
          <InvoiceUploader onChanged={refresh} onOpen={openExpense} />
          <ExpensesView
            expenses={periodExpenses}
            review={review}
            onReviewChange={(value) =>
              setParam({ revisar: value ? "1" : null })
            }
            onEdit={openExpense}
          />
        </div>
      ) : view === "ingresos" ? (
        <IncomesView
          rows={periodIncomes}
          onEdit={(income) => setIncomeDialog({ open: true, income })}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSupplierDialog({ open: true })}
            >
              <Plus className="h-4 w-4" />
              Nuevo proveedor
            </Button>
          </div>
          <SuppliersView
            suppliers={suppliers}
            expenses={periodExpenses}
            onEdit={(supplier) => setSupplierDialog({ open: true, supplier })}
          />
        </div>
      )}

      <ExpenseDialog
        open={expenseDialog.open}
        expense={expenseDialog.expense}
        suppliers={suppliers}
        onOpenChange={(open) =>
          setExpenseDialog((state) => ({ ...state, open }))
        }
        onSaved={refresh}
      />
      <IncomeDialog
        open={incomeDialog.open}
        income={incomeDialog.income}
        onOpenChange={(open) =>
          setIncomeDialog((state) => ({ ...state, open }))
        }
        onSaved={refresh}
      />
      <AccountingSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onSaved={refresh}
      />
      <SendToGestorDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        period={period}
        expenses={expenses}
        incomes={incomeRows}
        summary={summary}
        alerts={alerts}
        settings={settings}
        submissions={submissions}
        onSent={refresh}
        onOpenSettings={() => {
          setSendOpen(false);
          setSettingsOpen(true);
        }}
      />
      <SupplierDialog
        open={supplierDialog.open}
        supplier={supplierDialog.supplier}
        onOpenChange={(open) =>
          setSupplierDialog((state) => ({ ...state, open }))
        }
        onSaved={refresh}
      />
    </div>
  );
};

AccountingPage.path = "/dinero/contabilidad";
